/**
 * Home (spec 2026-09-23): a page of widgets, the shell in the core, the
 * widgets from the core registry and the custom manifest, drawn through the
 * slot adapter (strict access, one fence each). The layout is the viewer's
 * `ui.home` resolved over every registered widget; a widget the viewer may
 * not see stays in the preference and is skipped at draw (D3).
 *
 * Customize (Task 14, spec §4, D3): every change writes through
 * `setHomePrefs` immediately (no Save) — drag (dnd-kit) or the arrows both
 * move by widget id in the STORED (full) layout, never by the visible
 * index, so a hidden entry keeps its place relative to the rest.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SlidersHorizontal } from 'lucide-react'
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { HomeWidgetSlot, useHomeWidgets } from '@/components/custom/CustomSlots'
import type { HomeWidgetEntry, HomeWidth } from '@/components/custom/homeWidgetTypes'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import { useFeatureStore } from '@/stores/featureStore'
import { usePermissionStore } from '@/stores/permissionStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { AddWidgetList } from './AddWidgetList'
import { HomeCustomizeBar } from './HomeCustomizeBar'
import { HomeEmpty } from './HomeEmpty'
import { HomeWidgetCard } from './HomeWidgetCard'
import { GRID_CLASS } from './homeFlow'
import { useCoreHomeWidgets } from './homeWidgets'
import { addable, addWidget, canDrop, dragOrder, drawItems, laneNeighbour, moveWidget, removeWidget, resolveLayout, setWidgetWidth, type HomeLayoutEntry } from './homeLayout'
import { laneCollisions, laneKeyboardCoordinates, laneSortingStrategy } from './laneDrag'
import { DRAG_INSTRUCTIONS, WIDTH_WORD, dragAnnouncements } from './homeWords'
import { TodayLine } from './TodayLine'
import { useHomeFlow } from './useHomeFlow'
import { Quiet } from './widgetParts'
import { tourAnchor } from '@/components/tour/tourAnchor'

/** The control to focus once the DOM reflects a Customize change: the Move button pressed (F17), the
 * width pressed, or the grip after a drop (SA-1332: at the list frame each can remount the card). */
type PendingFocus =
  | { kind: 'move'; id: string; dir: 'up' | 'down' }
  | { kind: 'width'; id: string; width: HomeWidth }
  | { kind: 'grip'; id: string }

/** One object for every render, so dnd-kit keeps the pointer sensor and its activators between renders. */
const POINTER_OPTIONS = { activationConstraint: { distance: 8 } }

/** The sortable items in lane order, with the strategy that moves them within their lane and the
 * keyboard coordinates that keep a keyboard drag in it. */
function laneSortable(visible: HomeLayoutEntry[]) {
  const order = dragOrder(visible)
  return {
    ids: order.ids,
    strategy: laneSortingStrategy(order.keys),
    keyboard: laneKeyboardCoordinates((a, o) => canDrop(visible, a, o, true), order),
  }
}

export function HomeDashboardPage() {
  const core = useCoreHomeWidgets()
  const { all, canSee } = useHomeWidgets(core)
  const prefs = useSettingsStore((s) => s.ui.home)
  const setHomePrefs = useSettingsStore((s) => s.setHomePrefs)
  const zone = useDefaultTimezone()
  const [editing, setEditing] = useState(false)
  const layout = useMemo(() => resolveLayout(prefs.widgets, all), [prefs.widgets, all])
  const visible = useMemo(() => layout.filter((e) => canSee(e.id)), [layout, canSee])
  const byId = useMemo(() => new Map(all.map((w) => [w.id, w])), [all])
  /** SA-1406: the stage and the headline cards from the grid's own width and each visible card's minimum. */
  const sized = useMemo(() => visible.map((e) => ({ id: e.id, width: e.width, minRem: byId.get(e.id)!.minWidth })), [visible, byId])
  const [gridRef, flow, latestFlow] = useHomeFlow(sized)
  const lanes = flow.stage === 'lanes'

  /** SA-475 (§13 FF7): the announcements read titles, and dnd-kit's live
   * region and hidden instructions — which it always renders, with no switch
   * — go to a host drawn only while Customize is open; otherwise to a
   * DETACHED element that is never in the document. The grid keeps its tree,
   * so no widget remounts when Customize opens. */
  const announcements = useMemo(() => dragAnnouncements((id) => byId.get(id)?.title ?? 'A widget'), [byId])
  const [a11yHost, setA11yHost] = useState<HTMLDivElement | null>(null)
  const [detached] = useState(() => document.createElement('div'))

  // SA-475 (§13 FF4): strict slot access reads every widget as invisible
  // until both stores load, so the empty line waits for them — never a flash.
  const featuresLoaded = useFeatureStore((s) => s.isLoaded)
  const permissionsLoaded = usePermissionStore((s) => s.isLoaded)
  const storesLoaded = featuresLoaded && permissionsLoaded
  const candidates = addable(layout, all, canSee) as HomeWidgetEntry[]

  /** Carried from Task 13's review: stable PER WIDGET ID across renders (a
   * fresh closure every render would loop a widget that lists it in an
   * effect's deps) — cached in a ref, keyed by id, and never invalidated
   * since it only ever reads through `getState()`. */
  const optionSettersRef = useRef(new Map<string, (key: string, value: string) => void>())
  const setOption = useCallback((id: string) => {
    let fn = optionSettersRef.current.get(id)
    if (!fn) {
      fn = (key: string, value: string) => {
        const current = useSettingsStore.getState().ui.home.options
        setHomePrefs({ options: { ...current, [id]: { ...(current[id] ?? {}), [key]: value } } })
      }
      optionSettersRef.current.set(id, fn)
    }
    return fn
  }, [setHomePrefs])

  const commit = useCallback((next: HomeLayoutEntry[]) => setHomePrefs({ widgets: next }), [setHomePrefs])
  const indexOf = useCallback((id: string) => layout.findIndex((e) => e.id === id), [layout])

  /** In lanes a card drags within its lane (a full card is a partner of both), by pointer or keyboard;
   * in pairs and one column, the flat list. Memoized on what they read, so the sortable context keeps one identity
   * between renders. */
  const dnd = useMemo(() => lanes
    ? { collision: laneCollisions((a, o) => canDrop(visible, a, o, true)), ...laneSortable(visible) }
    : { collision: closestCenter, ids: visible.map((e) => e.id), strategy: rectSortingStrategy, keyboard: sortableKeyboardCoordinates },
  [lanes, visible])
  // Memoized on the getter, so the keyboard sensor (and the sensors list) keep one identity while the lanes hold.
  const keyboardOptions = useMemo(() => ({ coordinateGetter: dnd.keyboard }), [dnd.keyboard])
  const sensors = useSensors(
    useSensor(PointerSensor, POINTER_OPTIONS),
    useSensor(KeyboardSensor, keyboardOptions),
  )
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    // Defense in depth: dnd-kit runs our collision detection for every sensor, so `over` is already
    // filtered, but a stage flip mid-drag leaves an `over` that `closestCenter` chose.
    if (!canDrop(visible, String(active.id), String(over.id), lanes)) return
    // A drop that crosses runs remounts the card, so dnd-kit's own focus restore targets a detached grip.
    pendingFocusRef.current = { kind: 'grip', id: String(active.id) }
    commit(moveWidget(layout, indexOf(String(active.id)), indexOf(String(over.id))))
  }

  /** F17 (controller ruling): after a Move click, focus returns to the SAME
   * Move button on the moved card at its new position (repeated presses keep
   * moving it); if that would land the button disabled (the card has no further
   * neighbour in its lane that way), focus falls to the card's OTHER Move button instead. Decided
   * synchronously (we know the new position before committing) and applied
   * in an effect once the DOM reflects the move. At the list frame a card's parent depends on its
   * lane, so a width press or a drop can remount it too: the width press refocuses the width it
   * pressed, a drop the card's grip. A width press can also change the page's stage (SA-1406), which draws one render
   * later and remounts the card again, so the focus waits until the stage it changes to has been drawn. */
  const pendingFocusRef = useRef<PendingFocus | null>(null)
  useEffect(() => {
    const pending = pendingFocusRef.current
    if (!pending || latestFlow() !== flow) return
    pendingFocusRef.current = null
    const entry = byId.get(pending.id)
    if (!entry) return
    if (pending.kind === 'width') {
      const group = [...document.querySelectorAll<HTMLElement>('[role="group"][aria-label]')]
        .find((g) => g.getAttribute('aria-label') === `Width of ${entry.title}`)
      const word = WIDTH_WORD[pending.width]
      for (const btn of group?.querySelectorAll<HTMLButtonElement>('button') ?? []) {
        if (btn.textContent === word) { btn.focus(); break }
      }
      return
    }
    const label = pending.kind === 'move' ? `Move ${entry.title} ${pending.dir}` : `Drag ${entry.title}`
    for (const btn of document.querySelectorAll<HTMLButtonElement>('button[aria-label]')) {
      if (btn.getAttribute('aria-label') === label) { btn.focus(); break }
    }
  }, [visible, byId, flow, latestFlow])

  /** Fix item 4: HomeEmpty's own "Add a widget" button unmounts the instant
   * it opens Customize (it renders only on an empty Home), so without help
   * focus falls to <body>. Only THAT opening moves focus — the header's
   * Customize button stays focused on itself, as it always has. */
  const pendingEmptyAddFocusRef = useRef(false)
  useEffect(() => {
    if (!editing || !pendingEmptyAddFocusRef.current) return
    pendingEmptyAddFocusRef.current = false
    const region = document.querySelector<HTMLElement>('[role="region"][aria-label="Add a widget"]')
    if (!region) return
    const firstAdd = region.querySelector<HTMLButtonElement>('button[aria-label^="Add "]')
    ;(firstAdd ?? region).focus()
  }, [editing])

  /** Arrows move past the neighbour in the card's lane (or the adjacent visible card below the list frame), in the FULL layout's indices —
   * a hidden entry between them keeps its own stored position (the ordinary
   * `moveWidget` splice: everything untouched keeps its relative order, so a
   * hidden entry sitting between the mover and its visible neighbour is
   * neither dropped nor duplicated, only shifted like any other splice). */
  const moveBy = (id: string, direction: -1 | 1) => {
    const neighbour = laneNeighbour(visible, id, direction, lanes)
    if (!neighbour) return
    const next = moveWidget(layout, indexOf(id), indexOf(neighbour.id))
    const clickedDir: 'up' | 'down' = direction === -1 ? 'up' : 'down'
    const otherDir: 'up' | 'down' = direction === -1 ? 'down' : 'up'
    // The mover has reached its end when, on the visible list after the move, it has no further neighbour that way.
    const nowVisible = next.filter((e) => canSee(e.id))
    const landsAtEnd = laneNeighbour(nowVisible, id, direction, lanes) === null
    pendingFocusRef.current = { kind: 'move', id, dir: landsAtEnd ? otherDir : clickedDir }
    commit(next)
  }

  const widthOf = (id: string, width: HomeWidth) => {
    pendingFocusRef.current = { kind: 'width', id, width }
    commit(setWidgetWidth(layout, id, width, all))
  }

  /** One card, shared by the flat grid and the lanes. The slot gets the STORED width, never the drawn span. Cards
   * key `card:<id>` and column pairs `lanes:<key>`: a widget id can be any string, `top` included (SA-1344). */
  const renderCard = (e: HomeLayoutEntry, span: HomeWidth | null) => {
    const entry = byId.get(e.id)!
    const headline = flow.headlines.has(e.id)
    return (
      <HomeWidgetCard
        key={`card:${e.id}`} entry={entry} width={e.width} stage={flow.stage} span={span} headline={headline} editing={editing}
        canMoveUp={laneNeighbour(visible, e.id, -1, lanes) !== null} canMoveDown={laneNeighbour(visible, e.id, 1, lanes) !== null}
        onMove={(d) => moveBy(e.id, d)}
        onWidth={(w) => widthOf(e.id, w)}
        onRemove={() => commit(removeWidget(layout, e.id))}
      >
        <HomeWidgetSlot entry={entry} view={headline ? 'headline' : 'full'} width={e.width} options={prefs.options[e.id] ?? {}} setOption={setOption(e.id)} editing={editing}
          fallback={<><span className="text-[11px] font-semibold uppercase tracking-[.08em] text-muted-foreground">{entry.title}</span><Quiet><span role="status">This widget could not be shown.</span></Quiet></>} />
      </HomeWidgetCard>
    )
  }

  return (
    <PageFrame width="list" className="space-y-4">
      <PageHeader
        title="Home"
        sticky
        subtitle={<TodayLine zone={zone} />}
        actions={!editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} {...tourAnchor('home.header.customize')}>
            <SlidersHorizontal className="size-4" /> Customize
          </Button>
        )}
      />
      {editing && (
        <HomeCustomizeBar onReset={() => setHomePrefs({ widgets: null })} onDone={() => setEditing(false)} />
      )}
      {editing && <div ref={setA11yHost} />}
      {!editing && storesLoaded && visible.length === 0 && (
        <HomeEmpty canAdd={candidates.length > 0} onAdd={() => { pendingEmptyAddFocusRef.current = true; setEditing(true) }} />
      )}
      <DndContext sensors={sensors} collisionDetection={dnd.collision} onDragEnd={onDragEnd}
        accessibility={{ announcements, screenReaderInstructions: DRAG_INSTRUCTIONS, container: editing && a11yHost ? a11yHost : detached }}>
        <SortableContext items={dnd.ids} strategy={dnd.strategy} disabled={!editing}>
          <div ref={gridRef} {...tourAnchor('home.widgets.grid')} className={GRID_CLASS[flow.stage]}>
            {drawItems(visible, lanes).map((item) => item.kind === 'card'
              ? renderCard(item.entry, item.span)
              : (
                <Fragment key={`lanes:${item.key}`}>
                  <div data-lane="main" className="col-span-2 flex flex-col gap-4">{item.main.map((e) => renderCard(e, null))}</div>
                  <div data-lane="rail" className="col-span-1 flex flex-col gap-4">{item.rail.map((e) => renderCard(e, null))}</div>
                </Fragment>
              ))}
          </div>
        </SortableContext>
      </DndContext>
      {editing && (
        <AddWidgetList widgets={candidates} onAdd={(w) => commit(addWidget(layout, w))} />
      )}
    </PageFrame>
  )
}
